import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, QrCode, Loader2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { LGAS, DISCIPLINES } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import MembershipCard from "@/components/MembershipCard";
import HireShare from "@/components/HireShare";

const ALL_EXPERTISE = ["all", ...DISCIPLINES.flatMap((d) => d.subcategories)];

export default function Directory() {
  const [params] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lga, setLga] = useState(params.get("lga") || "all");
  const [discipline, setDiscipline] = useState("all");
  const [expertise, setExpertise] = useState("all");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    const q = {};
    if (lga !== "all") q.lga = lga;
    if (discipline !== "all") q.discipline = discipline;
    if (expertise !== "all") q.expertise = expertise;
    if (search) q.search = search;
    api.get("/members", { params: q }).then((r) => setMembers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [lga, discipline, expertise, search]);

  return (
    <main className="pt-28 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="overline-label mb-3">Discover Talent</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Member Directory</h1>
        <p className="text-zinc-400 mt-3 max-w-xl">Browse registered creatives across all 13 LGAs. Filter by location and expertise to find talent to hire.</p>

        {/* Filters */}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input placeholder="Search name or skill..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#131519] border-white/10" data-testid="directory-search-input" />
          </div>
          <Select value={lga} onValueChange={setLga}>
            <SelectTrigger className="bg-[#131519] border-white/10" data-testid="directory-lga-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All LGAs</SelectItem>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={discipline} onValueChange={(v) => { setDiscipline(v); setExpertise("all"); }}>
            <SelectTrigger className="bg-[#131519] border-white/10" data-testid="directory-discipline-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Disciplines</SelectItem>{DISCIPLINES.map((d) => <SelectItem key={d.category} value={d.category}>{d.category}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={expertise} onValueChange={setExpertise}>
            <SelectTrigger className="bg-[#131519] border-white/10" data-testid="directory-expertise-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{ALL_EXPERTISE.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All Expertise" : s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="mt-4 text-sm text-zinc-500">{loading ? "Loading..." : `${members.length} member${members.length !== 1 ? "s" : ""} found`}</div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E6197E]" /></div>
        ) : members.length === 0 ? (
          <div className="py-24 text-center text-zinc-500">No members match your filters yet.</div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {members.map((m, i) => (
              <motion.button key={m.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
                onClick={() => setActive(m)} data-testid="directory-member-card"
                className="text-left rounded-2xl border border-white/10 bg-[#131519] overflow-hidden hover:border-[#E6197E]/50 transition-all group">
                <div className="aspect-[4/5] bg-zinc-800 overflow-hidden relative">
                  {m.photo_path ? <img src={fileUrl(m.photo_path)} alt={m.full_name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-5xl font-heading text-zinc-600">{m.full_name[0]}</div>}
                  <div className="absolute top-3 left-3 flex items-center gap-1 text-[11px] px-2 py-1 rounded-full glass text-white"><MapPin className="w-3 h-3" />{m.lga}</div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-base truncate">{m.full_name}</h3>
                  <div className="text-xs text-[#E6197E] font-medium mt-0.5">{m.expertise}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{m.discipline}</div>
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] text-zinc-400"><QrCode className="w-3.5 h-3.5" /> View Digital ID</div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-transparent border-0 shadow-none p-0 max-w-sm">
          <MembershipCard member={active} />
          {active?.bio && <p className="mt-4 text-center text-sm text-zinc-300 px-4">{active.bio}</p>}
          <div className="mt-5"><HireShare member={active} /></div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
